import { type Currency, formatAmount } from "@loot-os/api-contract";
import type { StaffLiveBreakListItemWire } from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Input } from "@loot-os/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { CreateHouseLiveBreakSheet } from "../components/CreateHouseLiveBreakSheet";
import { ErrorAlert } from "../components/ErrorAlert";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { ShortDate } from "../components/ShortDate";
import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import {
  endUserLabel,
  LIVE_BREAK_CLOSE_REASON_LABELS,
  LIVE_BREAK_KIND_FILTER_LABELS,
  LIVE_BREAK_KIND_FILTERS,
  LIVE_BREAK_STATE_BADGE,
  LIVE_BREAK_STATE_LABELS,
  LIVE_BREAK_STATUS_FILTER_LABELS,
  LIVE_BREAK_STATUS_FILTERS,
  LIVE_BREAK_VISIBILITY_FILTER_LABELS,
  LIVE_BREAK_VISIBILITY_FILTERS,
  type LiveBreaksSearch,
  liveBreakDurationText,
  liveBreakState,
  liveBreaksListFilter,
} from "../lib/liveBreakStaff";
import { ratePercentText } from "../lib/liveBreaks";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { useTenantRole } from "../lib/useTenantRole";

type Row = StaffLiveBreakListItemWire;

const columns = createDataTableColumns<Row>();

function buildColumns(tenantId: string, currency: Currency, now: Date) {
  const money = (value: unknown) => amountInputValue(Number(value), currency);
  return columns.columns([
    columns.accessor("title", {
      header: "Title",
      size: 240,
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/storefront/live-breaks/list/$liveBreakId"
          params={{ tenantId, liveBreakId: row.original.id }}
          className="block truncate font-medium hover:underline"
          title={getValue()}
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    columns.accessor((liveBreak) => LIVE_BREAK_STATE_LABELS[liveBreakState(liveBreak)], {
      id: "state",
      header: "Status",
      cell: ({ getValue, row }) => (
        <Badge
          variant={LIVE_BREAK_STATE_BADGE[liveBreakState(row.original)]}
          title={
            row.original.closeReason
              ? LIVE_BREAK_CLOSE_REASON_LABELS[row.original.closeReason]
              : undefined
          }
        >
          {getValue()}
        </Badge>
      ),
      meta: { filter: "text" },
    }),
    columns.accessor(
      (liveBreak) =>
        liveBreak.host
          ? endUserLabel({
              username: liveBreak.host.username,
              externalId: liveBreak.host.externalId ?? liveBreak.host.displayName,
            })
          : "House",
      {
        id: "host",
        header: "Host",
        cell: ({ getValue, row }) =>
          row.original.host ? (
            <Link
              to="/tenants/$tenantId/storefront/live-breaks/hosts/$userId"
              params={{ tenantId, userId: row.original.host.endUserId }}
              className="hover:underline"
            >
              {getValue()}
            </Link>
          ) : (
            <Badge variant="secondary">House</Badge>
          ),
        meta: { filter: "text" },
      },
    ),
    columns.accessor((liveBreak) => (liveBreak.isPublic ? "Public" : "Private"), {
      id: "visibility",
      header: "Listed",
      size: 96,
      meta: { filter: "text" },
    }),
    columns.accessor("queuedCount", {
      header: "Queue",
      size: 88,
      meta: { align: "end", filter: "number" },
    }),
    columns.accessor("viewerCount", {
      header: "Viewers",
      size: 96,
      meta: { align: "end", filter: "number" },
    }),
    columns.accessor((liveBreak) => liveBreak.totals.viewerOpens + liveBreak.totals.selfOpens, {
      id: "opens",
      header: "Opens",
      size: 88,
      meta: { align: "end", filter: "number" },
    }),
    columns.accessor(
      (liveBreak) => liveBreak.totals.viewerSpendCents + liveBreak.totals.selfSpendCents,
      {
        id: "spend",
        header: amountColumnHeader("Spend", currency),
        cell: ({ getValue }) => (
          <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
        ),
        meta: { align: "end", filter: "number", filterCents: true, csv: money },
      },
    ),
    columns.accessor(
      (liveBreak) => liveBreak.totals.viewerCommissionCents + liveBreak.totals.selfCommissionCents,
      {
        id: "commission",
        header: amountColumnHeader("Commission", currency),
        cell: ({ getValue, row }) =>
          row.original.isHouse ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="font-mono text-xs">{formatAmount(getValue(), currency)}</span>
          ),
        meta: { align: "end", filter: "number", filterCents: true, csv: money },
      },
    ),
    columns.accessor("commissionRateBps", {
      header: "Rate (%)",
      size: 96,
      cell: ({ getValue }) => {
        const rate = getValue();
        return rate === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="font-mono text-xs">{ratePercentText(rate)}</span>
        );
      },
      meta: {
        align: "end",
        csv: (value) => (typeof value === "number" ? ratePercentText(value) : ""),
      },
    }),
    columns.accessor("createdAt", {
      header: "Started",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
    columns.accessor((liveBreak) => liveBreakDurationText(liveBreak, now), {
      id: "length",
      header: "Length",
      size: 104,
      enableSorting: false,
    }),
  ]);
}

/** One of the list's filters as a select: its options plus "Any". */
function FilterSelect<T extends string>({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: T | undefined;
  options: readonly T[];
  labels: Record<T, string>;
  onChange: (value: T | undefined) => void;
}) {
  const items: Record<string, string> = { any: `Any ${label.toLowerCase()}`, ...labels };
  return (
    <Select
      items={items}
      value={value ?? "any"}
      onValueChange={(next) => onChange(next === "any" || next === null ? undefined : (next as T))}
    >
      <SelectTrigger aria-label={label} className="w-36" size="sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">{items.any}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {labels[option]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Live breaks (boxed's live-break rooms tool): every live break of the environment, newest first,
 * filtered by status, who runs it, whether it is listed and one host, all in the URL so a view is
 * a link. Each opens its page, where staff edit, End and Force close it and run its queue. Admins
 * create a house live break here.
 */
export function LiveBreaksPage() {
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/live-breaks/list" });
  const search = useSearch({ from: "/app/tenants/$tenantId/storefront/live-breaks/list" });
  const navigate = useNavigate({ from: "/tenants/$tenantId/storefront/live-breaks/list" });
  const trpc = useTRPC();
  const currency = useCurrency();
  const { can } = useTenantRole();
  const [now] = useState(() => new Date());

  // The search box types into local state and reaches the URL debounced.
  const [query, setQuery] = useState(search.q ?? "");
  const debouncedQuery = useDebounced(query.trim());
  useEffect(() => {
    if ((debouncedQuery || undefined) === search.q) return;
    void navigate({
      search: (prev) => ({ ...prev, q: debouncedQuery || undefined }),
      replace: true,
    });
  }, [debouncedQuery, search.q, navigate]);

  const setFilter = (patch: Partial<LiveBreaksSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const liveBreaks = useInfiniteQuery(
    trpc.liveBreaks.list.infiniteQueryOptions(
      { ...liveBreaksListFilter(search), limit: 50 },
      {
        getNextPageParam: (last) => last.nextCursor,
        // Keep the rows on screen while a new filter resolves.
        placeholderData: (previous) => previous,
      },
    ),
  );
  const rows = useMemo(
    () => liveBreaks.data?.pages.flatMap((page) => page.liveBreaks) ?? [],
    [liveBreaks.data],
  );
  const tableColumns = useMemo(
    () => buildColumns(tenantId, currency, now),
    [tenantId, currency, now],
  );
  const hostRow = search.host
    ? rows.find((liveBreak) => liveBreak.host?.endUserId === search.host)?.host
    : undefined;
  const isStale = liveBreaks.isPlaceholderData;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight">Live breaks</h1>
          <p className="text-sm text-muted-foreground">
            Every live break in this environment, its hosts' and the store's own. Open one to edit
            it, run its queue, End or Force close it.
          </p>
        </div>
        {can("admin") && <CreateHouseLiveBreakSheet />}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          aria-label="Search live breaks"
          placeholder="Search title or host"
          className="w-full sm:w-64"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <FilterSelect
          label="Status"
          value={search.status}
          options={LIVE_BREAK_STATUS_FILTERS}
          labels={LIVE_BREAK_STATUS_FILTER_LABELS}
          onChange={(status) => setFilter({ status })}
        />
        {!search.host && (
          <FilterSelect
            label="Host"
            value={search.kind}
            options={LIVE_BREAK_KIND_FILTERS}
            labels={LIVE_BREAK_KIND_FILTER_LABELS}
            onChange={(kind) => setFilter({ kind })}
          />
        )}
        <FilterSelect
          label="Listing"
          value={search.visibility}
          options={LIVE_BREAK_VISIBILITY_FILTERS}
          labels={LIVE_BREAK_VISIBILITY_FILTER_LABELS}
          onChange={(visibility) => setFilter({ visibility })}
        />
        {search.host && (
          <Badge variant="secondary" className="h-8 gap-1 pr-1">
            {`Host: ${
              hostRow
                ? endUserLabel({
                    username: hostRow.username,
                    externalId: hostRow.externalId ?? hostRow.displayName,
                  })
                : search.host
            }`}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Show every host"
              onClick={() => setFilter({ host: undefined })}
            >
              <X />
            </Button>
          </Badge>
        )}
        {isStale && <span className="text-sm text-muted-foreground">Loading…</span>}
      </div>

      {liveBreaks.error ? (
        <ErrorAlert message={liveBreaks.error.message} />
      ) : (
        <Card>
          <CardContent
            aria-busy={isStale}
            className={`flex flex-col gap-3 transition-opacity ${isStale ? "opacity-60" : ""}`}
          >
            {liveBreaks.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={tableColumns}
                data={rows}
                getRowId={(liveBreak) => liveBreak.id}
                search={false}
                csvName="live-breaks"
                emptyMessage={
                  search.q || search.status || search.kind || search.visibility || search.host
                    ? "No live breaks match these filters."
                    : "No live breaks yet."
                }
              />
            )}
            <LoadMoreButton
              hasNextPage={liveBreaks.hasNextPage}
              isFetchingNextPage={liveBreaks.isFetchingNextPage}
              onClick={() => void liveBreaks.fetchNextPage()}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
